import { lazy, Suspense, useEffect, useLayoutEffect, useRef, type ReactElement, type RefObject } from 'react';
import { CartDrawer } from '@/components/CartDrawer/CartDrawer';
import { ConsentBanner } from '@/components/ConsentBanner/ConsentBanner';
import { Footer } from '@/components/Footer/Footer';
import { Nav } from '@/components/Nav/Nav';
import { AuthProvider } from '@/context/AuthContext';
import { StoreProvider } from '@/context/StoreContext';
import { FunnelProvider } from '@/features/funnel';
import { ParallaxArm, ReadingProgress } from '@/lib/motion';
import { useScrollReveal } from '@/lib/motion/useScrollReveal';
import { RouterProvider, useRouter, type Route } from '@/lib/router';
import { ErrorBoundary } from './ErrorBoundary';
import { NotFound } from './NotFound';
import { applyPageMeta } from './pageMeta';
import './app.css';

const HomePage = lazy(() => import('@/pages/Home/HomePage'));
const SovereignStackPage = lazy(() => import('@/pages/SovereignStack/SovereignStackPage'));
const OhmGearPage = lazy(() => import('@/pages/OhmGear/OhmGearPage'));
const ShadowKingPage = lazy(() => import('@/pages/ShadowKing/ShadowKingPage'));
const ScientiaPage = lazy(() => import('@/pages/Scientia/ScientiaPage'));
const NostramMissionemPage = lazy(() => import('@/pages/NostramMissionem/NostramMissionemPage'));
const ArcanaPage = lazy(() => import('@/pages/Arcana/ArcanaPage'));
const KybalionPage = lazy(() => import('@/pages/Kybalion/KybalionPage'));
const ContactPage = lazy(() => import('@/pages/Contact/ContactPage'));
const SignInPage = lazy(() => import('@/pages/SignIn/SignInPage'));
const ProductPage = lazy(() => import('@/pages/Product/ProductPage'));
const PrivacyPage = lazy(() => import('@/pages/Legal/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/Legal/TermsPage'));
const RefundsPage = lazy(() => import('@/pages/Legal/RefundsPage'));
const ShippingPage = lazy(() => import('@/pages/Legal/ShippingPage'));
const DisclaimerPage = lazy(() => import('@/pages/Legal/DisclaimerPage'));

function renderPage(route: Route): ReactElement {
  switch (route.page) {
    case 'home':
      return <HomePage />;
    case 'sovereign-stack':
      return <SovereignStackPage />;
    case 'ohm-gear':
      return <OhmGearPage />;
    case 'shadow-king':
      return <ShadowKingPage />;
    case 'scientia':
      return <ScientiaPage />;
    case 'nostram-missionem':
      return <NostramMissionemPage />;
    case 'arcana':
      return <ArcanaPage />;
    case 'kybalion':
      return <KybalionPage />;
    case 'contact':
      return <ContactPage />;
    case 'sign-in':
      return <SignInPage />;
    case 'product':
      return <ProductPage handle={route.handle ?? ''} />;
    case 'privacy':
      return <PrivacyPage />;
    case 'terms':
      return <TermsPage />;
    case 'refunds':
      return <RefundsPage />;
    case 'shipping':
      return <ShippingPage />;
    case 'disclaimer':
      return <DisclaimerPage />;
    case 'not-found':
      return <NotFound />;
  }
}

/** Rendered inside the page's Suspense boundary, so it mounts only once the page has (HM-1). */
function ScrollRevealArm({
  scope,
  pageKey,
}: {
  readonly scope: RefObject<HTMLElement | null>;
  readonly pageKey: string;
}) {
  useScrollReveal(scope, pageKey);
  return null;
}

function PageFallback() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="app-loader__spinner" style={{ width: 30, height: 30 }} />
    </div>
  );
}

function Shell() {
  const { route, pathname, exiting, redirecting } = useRouter();
  const main = useRef<HTMLElement>(null);

  // ML-D13: the shell's title/description on hard load, in-app navigation and back/forward.
  // A layout effect, so a page's own (passive) title effect still has the last word.
  useLayoutEffect(() => {
    applyPageMeta(route.page);
  }, [route.page, pathname]);

  // Every page view starts at the top (production effect on page change).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <>
      <CartDrawer />
      <div>
        <Nav />
        <main ref={main} className={'page-content' + (exiting ? ' page-content--exit' : '')}>
          <Suspense fallback={<PageFallback />}>
            {redirecting ? (
              <PageFallback />
            ) : (
              <>
                {renderPage(route)}
                <ScrollRevealArm scope={main} pageKey={pathname} />
                <ParallaxArm scope={main} pageKey={pathname} />
              </>
            )}
          </Suspense>
        </main>
        <Footer />
        <ReadingProgress />
      </div>
      <ConsentBanner />
    </>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <RouterProvider>
        <AuthProvider>
          <StoreProvider>
            <FunnelProvider>
              <Shell />
            </FunnelProvider>
          </StoreProvider>
        </AuthProvider>
      </RouterProvider>
    </ErrorBoundary>
  );
}
